import type { AdminUserDetail } from "@custos/api-client";
import {
  Address,
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Notice,
  Row,
  Segmented,
  Skeleton,
  Spot,
  formatAmount,
  formatDateTime,
  isZeroAmount,
} from "@custos/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, Search } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { PageHeader } from "../components/AdminShell";
import { StepUpModal } from "../components/StepUpModal";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { centsToInput, DOLLARS, inputToDollars, usd } from "../lib/format";
import { useCan } from "../lib/permissions";
import { qk, useLedger, useLimits, useUser, useUsers } from "../lib/queries";
import { DepositList, WithdrawalList } from "./Queues";
import { SwapList } from "./Swaps";

export function UsersPage() {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const users = useUsers(search, page);

  return (
    <>
      <PageHeader title="Users" subtitle="Search by email or user id." />
      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(0);
          setSearch(input.trim());
        }}
      >
        <Input
          aria-label="Search users"
          placeholder="name@example.com"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="max-w-md"
        />
        <Button type="submit" variant="secondary">
          <Search className="size-4" aria-hidden /> Search
        </Button>
      </form>
      {users.isPending ? (
        <Skeleton className="h-40 rounded-card" />
      ) : users.error ? (
        <Notice tone="danger">{errorMessage(users.error)}</Notice>
      ) : users.data.users.length === 0 ? (
        <Card>
          <EmptyState icon={<Spot name="addresses" />} title="No users found" />
        </Card>
      ) : (
        <>
          <ul className="stagger flex flex-col gap-2" data-testid="user-list">
            {users.data.users.map((u) => (
              <li key={u.id}>
                <Link
                  to={`/users/${u.id}`}
                  className="flex items-center gap-3 rounded-card bg-surface p-4 transition-colors duration-200 hover:bg-hover"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-fg">{u.email}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge tone={u.status === "ACTIVE" ? "success" : "danger"}>
                        {u.status.toLowerCase()}
                      </Badge>
                      <Badge tone={u.totpEnabled ? "neutral" : "warning"}>
                        {u.totpEnabled ? "2FA on" : "2FA off"}
                      </Badge>
                      {!u.emailVerified && <Badge tone="warning">email not verified</Badge>}
                      {u.kycFlag && <Badge tone="primary">KYC flag</Badge>}
                      {u.hasCustomLimits && <Badge tone="primary">custom limits</Badge>}
                      {u.lockedUntil && <Badge tone="danger">locked out</Badge>}
                    </div>
                  </div>
                  <span className="hidden text-sm text-muted sm:inline">{formatDateTime(u.createdAt)}</span>
                  <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
          <Pager page={page} total={users.data.total} pageSize={users.data.pageSize} onPage={setPage} />
        </>
      )}
    </>
  );
}

export function Pager({
  page,
  total,
  pageSize,
  onPage,
}: {
  page: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-center gap-3 text-sm">
      <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <span className="text-muted">
        Page {page + 1} of {pages}
      </span>
      <Button size="sm" variant="secondary" disabled={page + 1 >= pages} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </div>
  );
}

export function UserDetailPage() {
  const { id = "" } = useParams();
  const user = useUser(id);
  const can = useCan();
  const tabs = (
    [
      { value: "withdrawals", label: "Withdrawals", ok: can("queues.view") },
      { value: "deposits", label: "Deposits", ok: can("queues.view") },
      { value: "swaps", label: "Swaps", ok: can("swaps.view") },
      { value: "ledger", label: "Ledger", ok: can("users.ledger") },
    ] as const
  ).filter((t) => t.ok);
  const [picked, setTab] = useState<"withdrawals" | "deposits" | "swaps" | "ledger">("withdrawals");
  const tab = tabs.some((t) => t.value === picked) ? picked : tabs[0]?.value;

  return (
    <>
      <Link
        to="/users"
        className="mb-3 inline-flex min-h-11 items-center gap-1 text-sm text-muted hover:text-fg"
      >
        <ArrowLeft className="size-4" aria-hidden /> Users
      </Link>
      {user.isPending ? (
        <Skeleton className="h-64 rounded-card" />
      ) : user.error ? (
        <Notice tone="danger">{errorMessage(user.error)}</Notice>
      ) : (
        <>
          <PageHeader title={user.data.email} subtitle={`User id ${user.data.id}`} />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <ProfileCard user={user.data} />
            <BalancesCard user={user.data} />
            <LimitsCard user={user.data} />
          </div>
          {tab && (
            <div className="mt-6">
              <Segmented
                label="User history"
                value={tab}
                onChange={setTab}
                className="mb-4"
                options={tabs.map(({ value, label }) => ({ value, label }))}
              />
              {tab === "withdrawals" && <WithdrawalList status="ALL" userId={id} />}
              {tab === "deposits" && <DepositList status="ALL" userId={id} />}
              {tab === "swaps" && <SwapList userId={id} />}
              {tab === "ledger" && <LedgerTable userId={id} />}
            </div>
          )}
        </>
      )}
    </>
  );
}

function useRefreshUser(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: qk.user(id) });
    void queryClient.invalidateQueries({ queryKey: ["users"] });
  };
}

function ProfileCard({ user }: { user: AdminUserDetail }) {
  const refresh = useRefreshUser(user.id);
  const [blockOpen, setBlockOpen] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);
  const [reason, setReason] = useState("");
  const blocked = user.status === "BLOCKED";
  const canManage = useCan()("users.manage");

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle>Profile</CardTitle>
        <Badge tone={blocked ? "danger" : "success"} data-testid="user-status">
          {user.status.toLowerCase()}
        </Badge>
      </div>
      <div>
        <Row label="Email verified">{user.emailVerified ? "yes" : "no"}</Row>
        <Row label="2FA">{user.totpEnabled ? "on" : "off"}</Row>
        <Row label="KYC flag (placeholder)">{user.kycFlag ? "set" : "not set"}</Row>
        <Row label="Active sessions">{user.activeSessions}</Row>
        <Row label="Last active">{user.lastActiveAt ? formatDateTime(user.lastActiveAt) : "—"}</Row>
        <Row label="Joined">{formatDateTime(user.createdAt)}</Row>
        <Row label="Derivation index">{user.derivationIndex}</Row>
        {user.lockedUntil && <Row label="Locked out until">{formatDateTime(user.lockedUntil)}</Row>}
      </div>
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={blocked ? "primary" : "danger"}
            size="sm"
            onClick={() => {
              setReason("");
              setBlockOpen(true);
            }}
          >
            {blocked ? "Unblock user" : "Block user"}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setKycOpen(true)}>
            {user.kycFlag ? "Clear KYC flag" : "Set KYC flag"}
          </Button>
        </div>
      )}
      <StepUpModal
        open={blockOpen}
        onOpenChange={setBlockOpen}
        title={blocked ? "Unblock this user?" : "Block this user?"}
        description={
          blocked ? "They can log in again." : "They are logged out everywhere and can't log in or withdraw."
        }
        confirmLabel={blocked ? "Unblock" : "Block user"}
        danger={!blocked}
        canConfirm={reason.trim().length > 0}
        onConfirm={(code) => api.setUserBlocked(user.id, !blocked, reason.trim(), code)}
        onDone={refresh}
      >
        <Field label="Reason (for the audit log)">
          {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />}
        </Field>
      </StepUpModal>
      <StepUpModal
        open={kycOpen}
        onOpenChange={setKycOpen}
        title={user.kycFlag ? "Clear the KYC flag?" : "Set the KYC flag?"}
        description="A placeholder marker until a KYC provider is connected."
        confirmLabel="Save"
        onConfirm={(code) => api.setKycFlag(user.id, !user.kycFlag, code)}
        onDone={refresh}
      />
    </Card>
  );
}

function BalancesCard({ user }: { user: AdminUserDetail }) {
  const funded = (user.balances ?? []).filter((b) => !isZeroAmount(b.balance));
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>Balances</CardTitle>
      {user.balances === null ? (
        <p className="text-sm text-muted">Your role can't see balances.</p>
      ) : funded.length === 0 ? (
        <p className="text-sm text-muted">No funds.</p>
      ) : (
        <div data-testid="user-balances">
          {funded.map((b) => (
            <Row key={`${b.chain}:${b.asset}`} label={`${b.asset} · ${b.chain}`}>
              <span className="font-mono tabular-nums">{formatAmount(b.balance, 8)}</span>
            </Row>
          ))}
        </div>
      )}
      <CardTitle className="mt-2 text-base">Deposit addresses</CardTitle>
      {user.depositAddresses.length === 0 ? (
        <p className="text-sm text-muted">None created yet.</p>
      ) : (
        user.depositAddresses.map((d) => (
          <div key={d.family} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted">{d.family}</span>
            <Address value={d.address} start={8} end={6} />
          </div>
        ))
      )}
    </Card>
  );
}

function LimitsCard({ user }: { user: AdminUserDetail }) {
  const refresh = useRefreshUser(user.id);
  const global = useLimits();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ maxPerWithdrawal: "", maxPer24h: "", approvalAbove: "", note: "" });
  const g = global.data?.policy;
  const l = user.limits;
  const canEdit = useCan()("limits.edit");
  const valid = [form.maxPerWithdrawal, form.maxPer24h, form.approvalAbove].every(
    (v) => !inputToDollars(v) || DOLLARS.test(inputToDollars(v)!),
  );

  const field = (
    key: "maxPerWithdrawal" | "maxPer24h" | "approvalAbove",
    labelText: string,
    globalCents: number | null | undefined,
  ) => (
    <Field label={labelText} hint={`Empty = global (${g ? usd(globalCents ?? null) : "…"})`}>
      {(p) => (
        <Input
          {...p}
          inputMode="decimal"
          placeholder="global"
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        />
      )}
    </Field>
  );

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <CardTitle>Withdrawal limits</CardTitle>
        {l ? <Badge tone="primary">custom</Badge> : <Badge>global</Badge>}
      </div>
      <div data-testid="user-limits">
        <Row label="Max per withdrawal">
          {usd(l?.maxPerWithdrawalUsdCents ?? g?.maxPerWithdrawalUsdCents ?? null)}
        </Row>
        <Row label="Max per 24 h">{usd(l?.maxPer24hUsdCents ?? g?.maxPer24hUsdCents ?? null)}</Row>
        <Row label="Approval above">{usd(l?.approvalAboveUsdCents ?? g?.approvalAboveUsdCents ?? null)}</Row>
      </div>
      {canEdit && (
        <Button
          variant="secondary"
          size="sm"
          className="self-start"
          onClick={() => {
            setForm({
              maxPerWithdrawal: centsToInput(l?.maxPerWithdrawalUsdCents ?? null),
              maxPer24h: centsToInput(l?.maxPer24hUsdCents ?? null),
              approvalAbove: centsToInput(l?.approvalAboveUsdCents ?? null),
              note: "",
            });
            setOpen(true);
          }}
        >
          Edit limits
        </Button>
      )}
      <StepUpModal
        open={open}
        onOpenChange={setOpen}
        title="Limits for this user"
        description="US dollars. Leave a field empty to use the global limit."
        confirmLabel="Save limits"
        canConfirm={valid}
        onConfirm={(code) =>
          api.setUserLimits(
            user.id,
            {
              maxPerWithdrawal: inputToDollars(form.maxPerWithdrawal),
              maxPer24h: inputToDollars(form.maxPer24h),
              approvalAbove: inputToDollars(form.approvalAbove),
              note: form.note.trim() || null,
            },
            code,
          )
        }
        onDone={refresh}
      >
        {field("maxPerWithdrawal", "Max per withdrawal ($)", g?.maxPerWithdrawalUsdCents)}
        {field("maxPer24h", "Max per 24 hours ($)", g?.maxPer24hUsdCents)}
        {field("approvalAbove", "Needs approval above ($)", g?.approvalAboveUsdCents)}
        <Field label="Note (optional)">
          {(p) => (
            <Input
              {...p}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              maxLength={300}
            />
          )}
        </Field>
        {!valid && <Notice tone="warning">Use dollar amounts like 50 or 12.50.</Notice>}
      </StepUpModal>
    </Card>
  );
}

function LedgerTable({ userId }: { userId: string }) {
  const [page, setPage] = useState(0);
  const ledger = useLedger(userId, page);
  if (ledger.isPending) return <Skeleton className="h-40 rounded-card" />;
  if (ledger.error) return <Notice tone="danger">{errorMessage(ledger.error)}</Notice>;
  if (ledger.data.entries.length === 0) {
    return (
      <Card>
        <EmptyState icon={<Spot name="history" />} title="No ledger entries" />
      </Card>
    );
  }
  return (
    <>
      <Card className="overflow-x-auto p-0 sm:p-0">
        <table className="w-full min-w-[640px] text-sm" data-testid="ledger-table">
          <thead>
            <tr className="border-b border-line text-left text-xs text-muted">
              <th className="px-4 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Asset</th>
              <th className="px-4 py-3 text-right font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Reference</th>
            </tr>
          </thead>
          <tbody>
            {ledger.data.entries.map((e) => (
              <tr key={e.id} className="border-b border-line last:border-0">
                <td className="px-4 py-3 whitespace-nowrap text-muted">{formatDateTime(e.createdAt)}</td>
                <td className="px-4 py-3 text-fg">{e.type.toLowerCase().replaceAll("_", " ")}</td>
                <td className="px-4 py-3 text-muted">
                  {e.asset} · {e.chain}
                </td>
                <td
                  className={`px-4 py-3 text-right font-mono tabular-nums ${e.amount.startsWith("-") ? "text-danger" : "text-success"}`}
                >
                  {e.amount.startsWith("-") ? "" : "+"}
                  {formatAmount(e.amount, 8)}
                </td>
                <td className="max-w-56 truncate px-4 py-3 font-mono text-xs text-muted" title={e.reference}>
                  {e.reference}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <Pager page={page} total={ledger.data.total} pageSize={ledger.data.pageSize} onPage={setPage} />
    </>
  );
}
